import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronDown,
  Eye,
  FileSearch,
  Inbox,
  Search,
} from "lucide-react";
import type { BaseQuote, LeadQuote } from "@ir/types";
import {
  calculateCustomerPricing,
  deriveMarketplacePricingSummary,
  formatCurrency,
  formatDateTime,
  formatPricingOgiIndicator,
  formatPricingOgiTooltip,
  fullName,
  findQuotes,
  getProviderAnnualPremium,
} from "@ir/services";
import {
  Alert,
  Badge,
  Button,
  Card,
  CopyableId,
  DetailList,
  EmptyState,
  Field,
  IconButton,
  Input,
  LoadingState,
  PageHeader,
  StatusPill,
  cn,
  staggerChildren,
} from "@ir/ui";

/**
 * Look up quotes by customer email address or marketplace reference.
 *
 * This is the tool support reaches for when a customer says "I did get a quote"
 * — so it searches on submit rather than as you type, and shows raw provider
 * results rather than a tidied summary.
 */
export function QuoteFinderPage() {
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState<string | null>(null);
  const [quotes, setQuotes] = useState<LeadQuote[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;

    setLoading(true);
    setError(null);
    setQuotes(null);
    setSubmitted(trimmed);

    try {
      setQuotes(await findQuotes(trimmed));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "That lookup failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader
        icon={FileSearch}
        title="Quote finder"
        description="Find quotes using a customer's email address or marketplace reference."
      />

      <Card className="mb-6">
        <form onSubmit={(event) => void onSubmit(event)} className="space-y-1.5">
          <Field
            label="Customer email or reference"
            htmlFor="finder-query"
            hint="Enter the complete email address or customer reference. Hyphens and letter case do not matter for references."
          >
            <div className="flex flex-col gap-3 sm:grid sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
              <Input
                id="finder-query"
                type="text"
                required
                icon={Search}
                placeholder="name@example.com or FF-7K4M9-2QDX7"
                value={query}
                autoComplete="off"
                onChange={(event) => setQuery(event.target.value)}
                className="w-full min-w-0"
              />
              <Button
                type="submit"
                icon={Search}
                loading={loading}
                size="md"
                className="w-full shrink-0 sm:w-auto sm:px-4"
              >
                Find quotes
              </Button>
            </div>
          </Field>
        </form>
      </Card>

      {error && (
        <Alert tone="danger" title="Lookup failed">
          {error}
        </Alert>
      )}

      {loading && <LoadingState message={`Searching for ${submitted}…`} />}

      {!loading && quotes && (
        <>
          <div className="mb-3 flex items-center gap-3">
            <h2 className="font-display text-sm font-semibold uppercase tracking-[0.1em] text-content-subtle">
              {quotes.length} result{quotes.length === 1 ? "" : "s"} for {submitted}
            </h2>
            <span aria-hidden="true" className="h-px flex-1 rule-gold" />
          </div>

          {quotes.length === 0 ? (
            <Card padded={false}>
              <EmptyState
                icon={Inbox}
                title="No matching quotes"
                description="Check the email or reference, or try the quotes list which also searches by name, phone and postcode."
                action={
                  <Link to="/quotes">
                    <Button variant="secondary">Go to quotes</Button>
                  </Link>
                }
              />
            </Card>
          ) : (
            <motion.ul
              variants={staggerChildren(0.05)}
              initial="hidden"
              animate="visible"
              className="space-y-3"
            >
              {quotes.map((quote) => (
                <motion.li
                  key={quote.id}
                  variants={{ hidden: { opacity: 0, y: 8 }, visible: { opacity: 1, y: 0 } }}
                >
                  <QuoteResult quote={quote} />
                </motion.li>
              ))}
            </motion.ul>
          )}
        </>
      )}
    </>
  );
}

function QuoteResult({ quote }: { quote: LeadQuote }) {
  const [expanded, setExpanded] = useState(false);
  const baseQuotes = quote.base_quotes ?? [];
  const pricingSummary = deriveMarketplacePricingSummary(baseQuotes);
  const displayStatus =
    pricingSummary.quotedProviderCount > 0 || pricingSummary.allExpectedProvidersResolved
      ? pricingSummary.overallStatus
      : (quote.state ?? "Pending");

  return (
    <Card padded={false}>
      <div className="flex flex-wrap items-center gap-3 p-4">
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
        >
          <ChevronDown
            size={16}
            aria-hidden="true"
            className={cn(
              "shrink-0 text-content-subtle transition-transform duration-200",
              expanded && "rotate-180",
            )}
          />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-content">
              {fullName([quote.driver?.[0]?.first_name, quote.driver?.[0]?.last_name])}
            </span>
            <span className="block truncate text-xs text-content-subtle">
              {quote.type ?? "Unknown type"} · {formatDateTime(quote.created_at)}
            </span>
          </span>
        </button>

        <StatusPill status={displayStatus} />
        <span title={formatPricingOgiTooltip(pricingSummary)}>
          <Badge
            tone="neutral"
            className="cursor-help underline decoration-dotted underline-offset-4"
          >
            {formatPricingOgiIndicator(pricingSummary)}
          </Badge>
        </span>
        <Link to={`/quotes/${quote.id}`}>
          <IconButton icon={Eye} label="Open full quote details" size="sm" />
        </Link>
      </div>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-line"
          >
            <div className="space-y-4 bg-surface-sunken p-4">
              <DetailList
                items={[
                  { label: "Internal quote ID", value: <CopyableId value={quote.id} truncate={false} /> },
                  {
                    label: "Customer reference",
                    value: quote.reference ? (
                      <CopyableId value={quote.reference} truncate={false} />
                    ) : null,
                    hideWhenEmpty: true,
                  },
                  { label: "Created", value: formatDateTime(quote.created_at) },
                  {
                    label: "Vehicle",
                    value: [quote.vehicle?.[0]?.make, quote.vehicle?.[0]?.model]
                      .filter(Boolean)
                      .join(" "),
                  },
                ]}
              />

              {baseQuotes.length === 0 ? (
                <p className="text-sm text-content-muted">
                  No provider has returned a price for this quote.
                </p>
              ) : (
                <ul className="space-y-2">
                  {baseQuotes.map((baseQuote, index) => {
                    const graniteCover = baseQuote.non_broker_quote_details?.cover;
                    const brokerQuotes = getCustomerFacingBrokerQuotes(baseQuote);
                    const isOpenGi = (baseQuote.provider ?? "").trim().toLowerCase() === "opengi";
                    const providerStatus = getProviderDisplayStatus(baseQuote);
                    return (
                      <li
                        key={baseQuote.id ?? index}
                        className="rounded-lg border border-line bg-surface p-3"
                      >
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium capitalize text-content">
                            {formatProviderName(baseQuote.provider)}
                          </span>
                          <StatusPill status={providerStatus} />
                          {baseQuote.non_broker_quote_details?.insurer && (
                            <Badge tone="gold">
                              {baseQuote.non_broker_quote_details.insurer}
                            </Badge>
                          )}
                        </div>
                        {graniteCover ? (
                          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                            {(["comprehensive", "tpft", "tp"] as const).map((key) => {
                              const providerPremium = getProviderAnnualPremium(
                                baseQuote.provider,
                                graniteCover[key],
                              );
                              if (providerPremium <= 0) return null;
                              const annual = calculateCustomerPricing(
                                baseQuote.provider ?? "granite",
                                providerPremium,
                              );
                              const tenMonths = calculateCustomerPricing(
                                baseQuote.provider ?? "granite",
                                providerPremium,
                                10,
                              );
                              const elevenMonths = calculateCustomerPricing(
                                baseQuote.provider ?? "granite",
                                providerPremium,
                                11,
                              );
                              return (
                                <FinderPriceChip
                                  key={key}
                                  label={key}
                                  sellingPrice={annual.totalSellingPrice}
                                  providerPremium={providerPremium}
                                  tenMonthly={tenMonths.finance?.monthlyPayment}
                                  elevenMonthly={elevenMonths.finance?.monthlyPayment}
                                />
                              );
                            })}
                          </div>
                        ) : brokerQuotes.length > 0 ? (
                          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                            {brokerQuotes.map((brokerQuote) => {
                              const annual = calculateCustomerPricing(
                                baseQuote.provider ?? "",
                                brokerQuote.premium,
                              );
                              const tenMonths = calculateCustomerPricing(
                                baseQuote.provider ?? "",
                                brokerQuote.premium,
                                10,
                              );
                              const elevenMonths = calculateCustomerPricing(
                                baseQuote.provider ?? "",
                                brokerQuote.premium,
                                11,
                              );
                              return (
                                <FinderPriceChip
                                  key={brokerQuote.key}
                                  label={brokerQuote.coverType}
                                  sellingPrice={annual.totalSellingPrice}
                                  providerPremium={brokerQuote.premium}
                                  tenMonthly={tenMonths.finance?.monthlyPayment}
                                  elevenMonthly={elevenMonths.finance?.monthlyPayment}
                                />
                              );
                            })}
                          </div>
                        ) : isOpenGi ? (
                          <p className="text-xs text-content-subtle">
                            {getOpenGiSummary(baseQuote)}
                          </p>
                        ) : (
                          <p className="text-xs text-content-subtle">
                            {baseQuote.error ?? "No pricing returned."}
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}

function FinderPriceChip({
  label,
  sellingPrice,
  providerPremium,
  tenMonthly,
  elevenMonthly,
}: {
  label: string;
  sellingPrice: number;
  providerPremium: number;
  tenMonthly?: number;
  elevenMonthly?: number;
}) {
  return (
    <span className="rounded-lg border border-line bg-surface-sunken px-3 py-2 text-content-muted">
      <span className="block text-xs uppercase tracking-wide">{label}</span>
      <span className="block font-semibold tabular text-content">
        Customer {formatCurrency(sellingPrice)}
      </span>
      <span className="block text-xs">
        Provider {formatCurrency(providerPremium)}
      </span>
      <span className="block text-xs">
        10m {formatCurrency(tenMonthly)} · 11m {formatCurrency(elevenMonthly)}
      </span>
    </span>
  );
}

function formatProviderName(provider?: string | null): string {
  const normalized = (provider ?? "").trim().toLowerCase();
  if (normalized === "opengi") return "OpenGI";
  if (!normalized) return "Unknown provider";
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function getProviderDisplayStatus(baseQuote: BaseQuote): string | null | undefined {
  const normalizedProvider = (baseQuote.provider ?? "").trim().toLowerCase();
  if (normalizedProvider !== "opengi") {
    return baseQuote.state;
  }

  const ogi = baseQuote.provider_metadata?.ogi;
  if (!ogi) {
    return baseQuote.state;
  }

  if (ogi.prospect_reference?.trim()) {
    return "Prospect created";
  }

  if (ogi.send_to_broker_successful === true) {
    return ogi.send_to_broker_description?.trim() || "Delivered Successfully";
  }

  const brokerStatus = ogi.send_to_broker_status?.trim().toUpperCase();
  if (brokerStatus === "Q") {
    return ogi.send_to_broker_description?.trim() || "Queued for Delivery";
  }

  if (brokerStatus === "E" || brokerStatus === "ERROR" || ogi.send_to_broker_error_message) {
    return "Failed";
  }

  return ogi.send_to_broker_description?.trim() || baseQuote.state;
}

function normalizeBrokerPremium(provider: string | null | undefined, premium: number): number {
  const normalizedProvider = (provider ?? "").trim().toLowerCase();

  if (normalizedProvider === "inshur" && premium > 10000) {
    return premium / 100;
  }

  return premium;
}

function normalizeCoverType(value?: string | null): string {
  switch (value) {
    case "Third Party":
      return "Third Party Only";
    case "Third Party Fire and Theft":
      return "Third Party, Fire and Theft";
    default:
      return value?.trim() || "Quote";
  }
}

function getCustomerFacingBrokerQuotes(baseQuote: BaseQuote): Array<{
  key: string;
  coverType: string;
  premium: number;
}> {
  return (baseQuote.broker_quote_details ?? [])
    .filter((detail) => !detail.is_decline && (detail.premium ?? 0) > 0)
    .map((detail, index) => ({
      key: `${detail.uuid ?? index}-${detail.cover_type ?? "quote"}`,
      coverType: normalizeCoverType(detail.cover_type),
      premium: normalizeBrokerPremium(baseQuote.provider, detail.premium ?? 0),
    }));
}

function getOpenGiSummary(baseQuote: BaseQuote): string {
  const ogi = baseQuote.provider_metadata?.ogi;

  if (ogi?.prospect_reference?.trim()) {
    return `Prospect created (${ogi.prospect_reference.trim()}).`;
  }

  if (ogi?.send_to_broker_successful) {
    return ogi.send_to_broker_description?.trim() || "Delivered successfully to broker.";
  }

  const brokerStatus = ogi?.send_to_broker_status?.trim().toUpperCase();
  if (brokerStatus === "Q") {
    return ogi?.send_to_broker_description?.trim() || "Queued for broker delivery.";
  }

  if (ogi?.send_to_broker_error_message) {
    return ogi.send_to_broker_error_message;
  }

  return baseQuote.error ?? "No customer pricing returned from OpenGI.";
}
